import { useMemo, useState } from "react";

import { Box, Group, Table, Text, UnstyledButton } from "@mantine/core";
import { IconCaretDownFilled } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";


import { KpiTableResponse, KpiTableRow } from "types";

import classes from "./kpi.module.css";
import { getMetricLabelKey, getSubcategoryLabelKey } from "./kpiCatalog";
import {
  getAchievementPercent,
  KPI_ROW_BG_EVEN,
  KPI_ROW_BG_ODD,
  KPI_TEXT_DARK,
  KPI_TEXT_GRAY,
  resolveStatusColor,
} from "./kpiStatus";

interface KpiTableProps {
  table?: KpiTableResponse;
}

const formatValue = (value: number, unit?: string | null) =>
  unit === "CHF" ? `${value.toLocaleString("de-CH")} ${unit}` : String(value);

const KpiTable = ({ table }: KpiTableProps) => {
  const { t } = useTranslation();

  const metrics = useMemo<KpiTableRow[]>(() => table?.rows ?? [], [table]);

  // Subcategory groups start collapsed.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const expandableKeys = useMemo(
    () => metrics.filter((m) => (m.subcategories?.length ?? 0) > 0).map((m) => m.metric),
    [metrics],
  );
  const allExpanded = expandableKeys.length > 0 && expandableKeys.every((k) => expanded.has(k));

  const toggleRow = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  const toggleAll = () => setExpanded(allExpanded ? new Set() : new Set(expandableKeys));

  // Flatten to visible rows honoring collapsed state; zebra index is the visible row index.
  type ParentRow = { type: "parent"; row: KpiTableRow; label: string; isOpen: boolean };
  type SubRow = { type: "sub"; key: string; label: string; value: number; unit?: string | null };
  type VisibleRow = ParentRow | SubRow;

  const rows: VisibleRow[] = metrics.flatMap((m) => {
    const parentLabel = t(getMetricLabelKey(m.metric));
    const isOpen = expanded.has(m.metric);
    const out: VisibleRow[] = [{ type: "parent", row: m, label: parentLabel, isOpen }];

    if (isOpen) {
      (m.subcategories ?? []).forEach((sub) => {
        out.push({
          type: "sub",
          key: `${m.metric}.${sub.key}`,
          label: `${parentLabel} (${t(getSubcategoryLabelKey(sub.key))})`,
          value: sub.value,
          unit: m.unit,
        });
      });
    }

    return out;
  });

  return (
    <Box className={classes.card}>
      <Group justify="space-between" align="center" mb="md">
        <Text className={classes.cardTitle}>{t("profilePage.kpi.table.metricsTitle")}</Text>
        {expandableKeys.length > 0 ? (
          <UnstyledButton className={classes.expandAll} onClick={toggleAll}>
            {allExpanded
              ? t("profilePage.kpi.table.collapseAll")
              : t("profilePage.kpi.table.expandAll")}
          </UnstyledButton>
        ) : null}
      </Group>

      <Table className={classes.table} verticalSpacing="sm" horizontalSpacing="md">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("profilePage.kpi.table.metric")}</Table.Th>
            <Table.Th>{t("profilePage.kpi.table.realized")}</Table.Th>
            <Table.Th>{t("profilePage.kpi.table.target")}</Table.Th>
            <Table.Th>{t("profilePage.kpi.table.achievement")}</Table.Th>
            <Table.Th>{t("profilePage.kpi.table.status")}</Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {rows.map((row, index) => {
            const bg = index % 2 === 0 ? KPI_ROW_BG_ODD : KPI_ROW_BG_EVEN;

            if (row.type === "parent") {
              const m = row.row;
              const hasTarget = m.target !== null && m.target !== undefined && m.target > 0;
              const expandable = (m.subcategories?.length ?? 0) > 0;
              const percent =
                m.achievementPercent ?? (hasTarget ? getAchievementPercent(m.value, m.target as number) : 0);

              return (
                <Table.Tr key={m.metric} style={{ backgroundColor: bg }}>
                  <Table.Td>
                    {expandable ? (
                      <UnstyledButton
                        className={classes.rowToggle}
                        onClick={() => toggleRow(m.metric)}
                      >
                        <IconCaretDownFilled
                          size={14}
                          className={classes.chevron}
                          style={{ transform: row.isOpen ? "rotate(0deg)" : "rotate(-90deg)" }}
                        />
                        <Text size="sm" c={KPI_TEXT_DARK} fw={600}>
                          {row.label}
                        </Text>
                      </UnstyledButton>
                    ) : (
                      <Text size="sm" c={KPI_TEXT_DARK} fw={600}>
                        {row.label}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c={KPI_TEXT_DARK}>
                      {formatValue(m.value, m.unit)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c={KPI_TEXT_GRAY}>
                      {hasTarget ? formatValue(m.target as number, m.unit) : "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c={KPI_TEXT_GRAY}>
                      {hasTarget ? `${percent}%` : "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {hasTarget ? (
                      <span
                        className={classes.statusDot}
                        style={{
                          backgroundColor: resolveStatusColor(m.status, m.value, m.target as number),
                        }}
                      />
                    ) : (
                      <Text size="sm" c={KPI_TEXT_GRAY}>
                        —
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            }

            return (
              <Table.Tr key={row.key} style={{ backgroundColor: bg }}>
                <Table.Td>
                  <Text size="sm" c={KPI_TEXT_GRAY} pl={28}>
                    {row.label}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c={KPI_TEXT_DARK}>
                    {formatValue(row.value, row.unit)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c={KPI_TEXT_GRAY}>
                    —
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c={KPI_TEXT_GRAY}>
                    —
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c={KPI_TEXT_GRAY}>
                    —
                  </Text>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Box>
  );
};

export default KpiTable;
